'use client'

import React, { createContext, useEffect, useState } from 'react'

import { useLocale } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'

type ResourcesProviderProps = {
  children: React.ReactNode
}

export type NavigationLink = {
  id: string
  name: string
  to: string
  isRelativeUrl: boolean
  requiresLogin?: boolean
}

type LogoParams = {
  id: string
  logo: {
    file: {
      url: string
    }
  }
}

type navigationMenu = {
  id: string
  menuLinks: {
    id: string
    name: string
    links: NavigationLink[]
  }[]
  directLinks: NavigationLink[]
  omitLinks?: string[]
}

type ResourceState = {
  logo: { url: string }
  navigationMenu: navigationMenu | null
  isLoading: boolean
  isNavigationMenuLoading: boolean
}

const initalState: ResourceState = {
  logo: { url: '' },
  navigationMenu: null,
  isLoading: true,
  isNavigationMenuLoading: true,
}

export const ResourcesContext = createContext(initalState)

/**
 * This is the provider we will use for the base resources of our app,
 * we can add other core resources to this context moving forward
 */
const ResourcesProvider: React.FC<ResourcesProviderProps> = ({
  children,
}: ResourcesProviderProps) => {
  const { api, tenant } = useApi()
  const { locale } = useLocale()
  const [resources, setResources] = useState(initalState)

  const {
    data,
    isLoading,
  }: { data: LogoParams | undefined; isLoading: boolean } =
    api.contentful.getById.useQuery(
      {
        key: tenant?.appLogoId,
        language: Boolean(locale) ? locale : tenant?.locale?.default,
        type: 'header',
      },
      {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    )

  const {
    data: navigationMenus,
    isLoading: isNavigationMenuLoading,
  }: { data: navigationMenu[] | undefined; isLoading: boolean } =
    api.contentful.getByType.useQuery(
      {
        type: 'navigationMenu',
        language: locale,
        languages: tenant?.locale?.supported,
      },
      {
        enabled: Boolean(locale),
      },
    )

  useEffect(() => {
    setResources((prev) => ({
      ...prev,
      isLoading,
    }))

    if (!isLoading && Boolean(data)) {
      setResources((prev) => ({
        ...prev,
        logo: {
          url: data?.logo?.file?.url ?? '',
        },
      }))
    }
  }, [isLoading])

  useEffect(() => {
    setResources((prev) => ({
      ...prev,
      isNavigationMenuLoading,
    }))

    if (!isNavigationMenuLoading && navigationMenus?.length) {
      setResources((prev) => ({
        ...prev,
        navigationMenu: navigationMenus?.[0] ?? null,
      }))
    }
  }, [isNavigationMenuLoading])

  return (
    <ResourcesContext.Provider value={resources}>
      {children}
    </ResourcesContext.Provider>
  )
}

export { ResourcesProvider }
